import { AxiosRequestConfig } from "axios";
import { UseRequestApi } from "../Api/RequestApi";
import { ContractKey, contractKeys as contractTypeKeys, contractTypes } from "../contractTypes";
import { PrepopFieldsQueryParams } from "../Schemas/PrepopFieldsQueryParams";
import { RequestFieldDto } from "../Schemas/RequestFieldDto";
import { RequestFieldEffectDto } from "../Schemas/RequestFieldEffectDto";
import { RequestFileDto } from "../Schemas/RequestFileDto";
import { RequestFormFieldDataInputSchema } from "../Schemas/RequestFormFieldDataInput";
import { RequestFormFieldDataOutput } from "../Schemas/RequestFormFieldDataOutput";
import { RequestHelperGetContractPrepopInfoOutput } from "../Schemas/RequestHelperGetContractPrepopInfoOutput";
import { RequestHelperGetPrepopInfoOutput } from "../Schemas/RequestHelperGetPrepopInfoOutput";
import { RequestParameterSectionDto } from "../Schemas/RequestParameterSectionDto";
import { RequestPoistkaDto } from "../Schemas/RequestPoistkaDto";
import { RequestStateDto } from "../Schemas/RequestStateDtoSchema";
import { RequestTypeDto } from "../Schemas/RequestTypeDto";
import { RequestTypeStepDto } from "../Schemas/RequestTypeStepDto";
import { SectionOrFieldGroupDto } from "../Schemas/SectionOrFieldGroupDto";
import { TaxonomyDto } from "../Schemas/TaxonomyDto";
import { extensionTester, utilities } from "./file";
import { cloneDeep } from "lodash";
import { MutableRefObject } from "react";
import { ExtendedFile } from "./UploadHelpers";
import { RequestParameterTypeEnum } from "../Enums/RequestParameterTypeEnum";
import { RequestSourceTypeEnum } from "../Enums/RequestSourceTypeEnum";

export const getAvailableContractsPrepoped = (prepopResults: RequestHelperGetPrepopInfoOutput): RequestPoistkaDto[] => {
    // For each contract set the type
    for (let i = 0; i < prepopResults.contracts.length; i++) {
        let contr = prepopResults.contracts[i];
        let contrType;

        // Get the contract type based on sector id
        for (let j = 0; j < contractTypeKeys.length; j++) {
            const key: ContractKey = contractTypeKeys[j];
            const sectorIds = contractTypes[key].sectorIds;

            if (sectorIds.indexOf(contr.odvetvieId) !== -1) {
                contrType = contractTypes[key];
                break;
            }
        }

        if (contrType) {
            contr.type = contrType;
            contr.titleToShow = contr.type.shortTitle;

            // Title
            if (contr.isGroupContract) {
                contr.titleToShow = contr.type.shortTitlePlural;
            }
        }

        // Stav pz
        if (contr.stavPZ && contr.stavPZ.length > 0) {
            contr.stavToShow =
                ' - ' +
                (contr.stavPZ.startsWith('Z')
                    ? 'aktívna'
                    : contr.stavPZ.startsWith('S')
                        ? 'stornovaná'
                        : '');
        }
    }

    return prepopResults.contracts;
}

export const getFieldsAndGroupsFromSteps = (steps: RequestTypeStepDto[]) => {
    let fields: RequestFieldDto[] = [];
    const sections: RequestParameterSectionDto[] = [];
    const groups: Record<string, SectionOrFieldGroupDto> = {};

    if (steps) {
        for (let i = 0; i < steps.length; i++) {
            let step = steps[i];

            if (step.sections) {
                for (let j = 0; j < step.sections.length; j++) {
                    const section: RequestParameterSectionDto = step.sections[j];

                    // If collapsed by default set it to collapsed
                    if (
                        section.collapsible &&
                        section.isCollapsedByDefault === true
                    ) {
                        section.collapsed = true;
                    }

                    let fS: RequestFieldDto[] = getFieldsFromSection(section, groups);

                    if (fS && fS.length > 0) {
                        for (let l = 0; l < fS.length; l++) {
                            fields.push(fS[l]);
                        }
                    }

                    let s = getSectionsFromSection(section);

                    if (s && s.length > 0) {
                        for (let l = 0; l < s.length; l++) {
                            sections.push(s[l]);
                        }
                    }
                }
            }
        }
    }

    fields = fields.sort((a, b) => {
        if (a.order > b.order) return 1;
        if (b.order > a.order) return -1;
        return 0;
    });

    return { fields: fields, groups: groups, sections: sections };
}

export type GetFieldsAndGroupsFromStepsReturn = ReturnType<typeof getFieldsAndGroupsFromSteps>;

const getFieldsFromSection = (section: RequestParameterSectionDto, groups: Record<string, SectionOrFieldGroupDto>): RequestFieldDto[] => {
    const fields: RequestFieldDto[] = [];

    if (section) {
        if (section.children && section.children.length) {
            for (let q = 0; q < section.children.length; q++) {
                let groupChild = section.children[q];

                if (groupChild.groupFieldType === 9) {
                    groups[groupChild.group!] = groupChild;
                }

                for (let s = 0; s < groupChild.children.length; s++) {
                    let fieldOrSection = groupChild.children[s];

                    if (fieldOrSection.section) {
                        let child = fieldOrSection.section;

                        //if collapsed by default set it to collapsed
                        if (
                            child.collapsible &&
                            child.isCollapsedByDefault === true
                        ) {
                            child.collapsed = true;
                        }

                        let chF = getFieldsFromSection(child, groups);

                        if (chF && chF.length > 0) {
                            for (let f = 0; f < chF.length; f++) {
                                fields.push(chF[f]);
                            }
                        }
                    } else if (fieldOrSection.field) {
                        let field = fieldOrSection.field;
                        fields.push(field);
                    }
                }
            }
        }
    }

    return fields;
}

const getSectionsFromSection = (section: RequestParameterSectionDto): RequestParameterSectionDto[] => {
    let sections: RequestParameterSectionDto[] = [];

    if (section) {
        sections.push(section);

        if (section.children && section.children.length) {
            for (let q = 0; q < section.children.length; q++) {
                let child = section.children[q];

                for (let s = 0; s < child.children.length; s++) {
                    let fieldOrSection = child.children[s];

                    if (fieldOrSection.section) {
                        let child2 = fieldOrSection.section;

                        let chS = getSectionsFromSection(child2);

                        if (chS && chS.length > 0) {
                            for (let f = 0; f < chS.length; f++) {
                                sections.push(chS[f]);
                            }
                        }
                    }
                }
            }
        }
    }

    return sections;
}

/// Function call /getFormFieldData to get definition of the form.
/// And also it maps fields, groups, sections and other flags of the form.
export const getRequestFieldData = async (requestType: RequestTypeDto, requestId: string | null, requestApi: UseRequestApi, showRemovedParameterTypes: boolean = false, config?: AxiosRequestConfig) => {
    const formFieldDataInput = RequestFormFieldDataInputSchema.parse({
        requestTypeId: requestType.id,
        requestId: requestId,
        showRemovedParameterTypes,
    });

    const result = await requestApi.getFormFieldData(formFieldDataInput, config);

    const fieldsAndGroups: GetFieldsAndGroupsFromStepsReturn = getFieldsAndGroupsFromSteps(result.steps);

    const taxonomy: TaxonomyDto[] = result.taxonomy || [];
    const requestHasAiDetection: boolean = taxonomy.some((t) => t.aiDetectionType && t.aiDetectionType > 0);

    const disableImmediateFileUpload =
        taxonomy.length === 0 ||
        taxonomy.some((t) => t.linkedButtons.length > 0 || t.linkedOdvetvie.length > 0 || t.isSqlValidation) ||
        (result.isManuallySigned == true && result.manualSignRequired == true);

    const attachmentUploadMessage = result.attachmentUploadInfo || "";
    const requestHasTaxonomies = taxonomy.length > 0;

    return {
        result,
        fieldsAndGroups,
        ...fieldsAndGroups,
        requestHasAiDetection,
        disableImmediateFileUpload,
        attachmentUploadMessage,
        requestHasTaxonomies
    }
}

export type GetRequestFieldDataReturn = ReturnType<typeof getRequestFieldData>;

export function getFromFieldDataSuccess(
    result: RequestFormFieldDataOutput,
    fields: RequestFieldDto[],
    groups: Record<string, SectionOrFieldGroupDto>,
    sections: RequestParameterSectionDto[],
    requestToSelect: RequestTypeDto
) {
    // Recursively get the fields from steps, because in this UI for now we dont support sections and steps
    const fieldsByKey: Record<string, RequestFieldDto> = {};
    for (let k = 0; k < fields.length; k++) {
        const field: RequestFieldDto = fields[k];
        const key = field.key;
        if (key) {
            fieldsByKey[key] = field;
        }
    }

    const taxonomy = result.taxonomy || [];

    const selectedRequest = {
        ...requestToSelect,
        manualSignRequired: result.manualSignRequired,
        isMagicLinkAvailable: result.isMagicLinkAvailable,
        ismanuallySigned: result.isManuallySigned,
        steps: result.steps,
        categories: taxonomy,
        fields,
        sections,
        itemsByGroup: groups,
        fieldsByKey,
        sourceTypeId: result.sourceTypeId
    }

    // This item.files might not be needed at all
    // Commented out for now because there is files in response from BE
    // selectedRequest.categories.forEach(function (item) {
    //     //@ts-ignore
    //     delete item.files;
    // });

    return selectedRequest;
}

export type Request = ReturnType<typeof getFromFieldDataSuccess> & {
    requestId?: string;
    requestState?: RequestStateDto[];
    isSubmitted?: boolean;
    stateAnswer?: string | null;
    hasGeneratedDocument?: boolean;
    files?: RequestFileDto[];
}

export type RequestWithFiles = Omit<Request, "files"> & { files: RequestFileDto[] };

export const isRequestWithFiles = (request: MutableRefObject<Request>): request is MutableRefObject<RequestWithFiles> => {
    return !!request.current.files
}

export const getRequestWithFiles = (request: MutableRefObject<Request>): MutableRefObject<RequestWithFiles> => {
    if (isRequestWithFiles(request)) return request;
    request.current = { ...request.current, files: [] };
    if (isRequestWithFiles(request)) return request;
    throw new Error("Request does not have files");
}

export function prepopulateFieldValues(
    fields: RequestFieldDto[],
    prepopResult: RequestHelperGetPrepopInfoOutput,
    prepopFieldsQueryParams: PrepopFieldsQueryParams
) {
    const setFieldValue = (field: RequestFieldDto, queryValue: any, partnerValue?: any) => {
        if (field.paramValue) return;
        if (queryValue) {
            field.paramValue = queryValue;
            field.prefilledFromQueryParam = true;
        } else if (partnerValue !== undefined) {
            field.paramValue = partnerValue;
        }
    };

    for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        if (field.uiHint === 'titlebeforename') {
            setFieldValue(field, prepopFieldsQueryParams.titleBefore, prepopResult?.partner?.titleBefore);
        } else if (field.uiHint === 'firstname') {
            setFieldValue(field, prepopFieldsQueryParams.firstName, prepopResult?.partner?.firstName);
        } else if (field.uiHint === 'lastname') {
            setFieldValue(field, prepopFieldsQueryParams.lastName, prepopResult?.partner?.lastName);
        } else if (field.uiHint === 'titleaftername') {
            setFieldValue(field, prepopFieldsQueryParams.titleAfter, prepopResult?.partner?.titleAfter);
        } else if (field.uiHint === 'birthnumber') {
            setFieldValue(field, prepopFieldsQueryParams.birthNumber, prepopResult?.partner?.birthNumber);
        } else if (field.uiHint === 'permstreet' || field.uiHint === 'deliverystreet') {
            setFieldValue(field, prepopFieldsQueryParams.permAddressStreet, prepopResult?.partner?.permAddressStreet);
        } else if (field.uiHint === 'permstreetnum' || field.uiHint === 'deliverystreetnum') {
            setFieldValue(field, prepopFieldsQueryParams.permAddressStreetNumber, prepopResult?.partner?.permAddressStreetNumber);
        } else if (field.uiHint === 'permcity' || field.uiHint === 'deliverycity') {
            setFieldValue(field, prepopFieldsQueryParams.permAddressCity, prepopResult?.partner?.permAddressCity);
        } else if (field.uiHint === 'permpostcode' || field.uiHint === 'deliverypostcode') {
            setFieldValue(field, prepopFieldsQueryParams.permAddressPostCode, prepopResult?.partner?.permAddressPostCode);
        } else if (field.uiHint === 'permcountry' || field.uiHint === 'deliverycountry') {
            setFieldValue(field, prepopFieldsQueryParams.permAddressCountry, prepopResult?.partner?.permAddressCountry);
        } else if (field.uiHint === 'phonenumber') {
            setFieldValue(field, prepopFieldsQueryParams.phoneNumber, prepopResult?.partner?.phoneNumber);
        } else if (field.uiHint === 'email') {
            setFieldValue(field, prepopFieldsQueryParams.email, prepopResult?.partner?.email);
        } else if (field.key === 'contractnumber') {
            setFieldValue(field, prepopFieldsQueryParams.insuranceNumber);
        } else if (field.key === 'partnerid') {
            setFieldValue(field, prepopFieldsQueryParams.partnerId);
        }
    }

    return fields;
}

export const createPathToProp = (propName: string, index: number) => {
    return `${propName}[${index}]`;
}

export function addIconsToFiles(filesToRead: ExtendedFile[] | RequestFileDto[], ignoreUploadCompleted: boolean, isOldFile: boolean) {
    for (let i = 0; i < filesToRead.length; i++) {
        let file: ExtendedFile | RequestFileDto = filesToRead[i];
        if (!isOldFile) {
            file.id = utilities.guid();
        }

        file.extension = file.name ? utilities.getFileExtension(file.name) : '';
        if (!ignoreUploadCompleted) {
            file.uploadCompleted = false;
        }

        /// I commented out appConfig.siteRoot because in the AppConfig.ts file, this value was set up as
        /// siteRoot: 'your site root'.
        /// I think it has not been working yet.
        if (extensionTester.fileIsExcel(file.extension)) {
            // file.iconUrl = appConfig.siteRoot + '/App/Content/icons/microsoft-excel.svg';
            file.iconTooltip = 'Excel dokument';
        } else if (extensionTester.fileIsWord(file.extension)) {
            // file.iconUrl = appConfig.siteRoot + '/App/Content/icons/microsoft-word.svg';
            file.iconTooltip = 'Word dokument';
        } else if (extensionTester.fileIsPowerPoint(file.extension)) {
            // file.iconUrl = appConfig.siteRoot + '/App/Content/icons/microsoft-powerpoint.svg';
            file.iconTooltip = 'Power Point dokument';
        } else if (extensionTester.fileIsImage(file.extension)) {
            file.icon = 'photo';
            file.iconTooltip = 'Obrázok';
        } else if (extensionTester.fileIsVideo(file.extension)) {
            file.icon = 'videocam';
            file.iconTooltip = 'Video';
        } else if (extensionTester.fileIsPdf(file.extension)) {
            file.iconText = 'PDF';
            file.iconTooltip = 'PDF dokument';
        } else if (extensionTester.fileIsText(file.extension)) {
            file.iconText = 'TXT';
            file.iconTooltip = 'Textový dokument';
        } else if (extensionTester.isOtherFileFormat(file.extension)) {
            file.icon = 'attachment';
            file.iconTooltip = 'Neznámy typ súboru';
        }
    }
}

export const prepopulateContractAddressFieldValues = (fields: RequestFieldDto[], prepopResult: RequestHelperGetContractPrepopInfoOutput): RequestFieldDto[] => {
    for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        if (field.uiHint === 'contractstreet') {
            field.paramValue = prepopResult.contractAddress?.addressStreet || "";
        } else if (field.uiHint === 'contractstreetnum') {
            field.paramValue = prepopResult.contractAddress?.addressStreetNumber || "";
        } else if (field.uiHint === 'contractcity') {
            field.paramValue = prepopResult.contractAddress?.addressCity || "";
        } else if (field.uiHint === 'contractpostcode') {
            field.paramValue = prepopResult.contractAddress?.addressPostCode || "";
        } else if (field.uiHint === 'contractcountry') {
            field.paramValue = prepopResult.contractAddress?.addressCountry || "";
        }
    }
    return fields;
}

export function shouldHideFieldOrSection(
    fieldOrSectionOriginal: RequestParameterSectionDto | RequestFieldDto,
    selectedRequest: Request,
    odvetvieId: number,
    fieldRelationEffects: RequestFieldEffectDto[] = []
) {
    let fieldOrSection: (RequestParameterSectionDto | RequestFieldDto) & {
        hidden?: boolean | null;
        childrenHandled?: boolean;
        children?: SectionOrFieldGroupDto[];
    } = cloneDeep(fieldOrSectionOriginal);

    // Field relation visibility: a field flagged with `fieldsRelationRequired` is driven solely by
    // the GetRequestFieldsRelation response. If the endpoint returned an effect for this field's key,
    // honor its `visibility`; otherwise (no effect yet) keep the field visible.
    if ("fieldsRelationRequired" in fieldOrSection && fieldOrSection.fieldsRelationRequired === true) {
        const relationKey = fieldOrSection.key;
        const effect = fieldRelationEffects.find((e) => e.key === relationKey);
        if (!effect) return true;
        return effect.visibility === false;
    }

    const isClientRequest = selectedRequest.sourceTypeId === RequestSourceTypeEnum.KLIENT;
    const isOriginalAddressField = fieldOrSection.name === 'Pôvodná adresa / Pôvodné sídlo firmy' || fieldOrSection.name === 'Pôvodná adresa';

    if (!isClientRequest && isOriginalAddressField) {
        return true;
    }

    const handleFalse = (): boolean => {
        if (fieldOrSection.children && fieldOrSection.hidden === true) {
            fieldOrSection.childrenHandled = false;
        }
        fieldOrSection.hidden = false;
        return false;
    }

    if (selectedRequest && selectedRequest.itemsByGroup && selectedRequest.fieldsByKey) {
        if (fieldOrSection.linkedOdvetvie) {
            if (fieldOrSection.linkedOdvetvie.length > 0) {
                if (odvetvieId && odvetvieId !== 0) {
                    // hide section if linkedOdvetvie = scopeOdvetvie and when = 2 OR linkedOdvetvie != scopeOdvetvie and when = 1
                    for (let i = 0; i < fieldOrSection.linkedOdvetvie.length; i++) {
                        // When = 2 -> at least one
                        if (fieldOrSection.linkedOdvetvie[i].linkedOdvetvieWhen === 2) {
                            if (fieldOrSection.linkedOdvetvie[i].linkedOdvetvieId === odvetvieId) {
                                if (fieldOrSection.children) {
                                    if (fieldOrSection.hidden === false) {
                                        fieldOrSection.childrenHandled = false;
                                    }
                                }

                                fieldOrSection.hidden = true;
                                return true;
                            }
                        }
                    }

                    let linkedWhen1 = [];

                    for (let i = 0; i < fieldOrSection.linkedOdvetvie.length; i++) {
                        // When = 1 -> every member
                        if (fieldOrSection.linkedOdvetvie[i].linkedOdvetvieWhen === 1) {
                            linkedWhen1.push(fieldOrSection.linkedOdvetvie[i].linkedOdvetvieId);
                        }
                    }

                    if (linkedWhen1.length > 0) {
                        if (linkedWhen1.indexOf(odvetvieId) < 0) {
                            if (fieldOrSection.children) {
                                if (fieldOrSection.hidden === false) {
                                    fieldOrSection.childrenHandled = false;
                                }
                            }

                            fieldOrSection.hidden = true;
                            return true;
                        }
                    }
                } else {
                    if (fieldOrSection.children) {
                        if (fieldOrSection.hidden === false) {
                            fieldOrSection.childrenHandled = false;
                        }

                        if (!fieldOrSection.childrenHandled) {
                        }
                    }

                    fieldOrSection.hidden = true;
                    return true;
                }
            }
        }

        if (fieldOrSection.linkedButtonKey) {
            let field = selectedRequest.fieldsByKey[fieldOrSection.linkedButtonKey];

            if (field && field.group) {
                let group = selectedRequest.itemsByGroup[field.group];

                // groups are made only for radiobuttons -> fieldtype = 9 (getFieldsFromSection)
                if (group) {
                    if (
                        (group.groupValue !== fieldOrSection.linkedButtonKey &&
                            fieldOrSection.linkedButtonWhen === 1) ||
                        (group.groupValue === fieldOrSection.linkedButtonKey &&
                            fieldOrSection.linkedButtonWhen === 2)
                    ) {
                        if (fieldOrSection.children) {
                            if (fieldOrSection.hidden === false) {
                                fieldOrSection.childrenHandled = false;
                            }

                            if (!fieldOrSection.childrenHandled) {
                            }
                        }

                        fieldOrSection.hidden = true;
                        return true;
                    }
                } else {
                    if (
                        (field.paramValue &&
                            fieldOrSection.linkedButtonWhen === 2) ||
                        (!field.paramValue &&
                            fieldOrSection.linkedButtonWhen === 1)
                    ) {
                        if (fieldOrSection.children) {
                            if (fieldOrSection.hidden === false) {
                                fieldOrSection.childrenHandled = false;
                            }

                            if (!fieldOrSection.childrenHandled) {
                            }
                        }

                        fieldOrSection.hidden = true;
                        return true;
                    }
                }
                return handleFalse();
            } else {
                return handleFalse();
            }
        } else {
            return handleFalse();
        }
    } else {
        return handleFalse();
    }
}

/** Checkbox groups: API sends groupFieldType 8 for 2+ checkboxes, or FE detects all-checkbox children. */
export function isCheckboxFieldGroup(group: SectionOrFieldGroupDto): boolean {
    if (group.groupFieldType === RequestParameterTypeEnum.CHECKBOX) return true;

    const fields = group.children?.map((c) => c.field).filter((f): f is RequestFieldDto => !!f) ?? [];
    return fields.length > 0
        && fields.length === (group.children?.length ?? 0)
        && fields.every((f) => f.type === RequestParameterTypeEnum.CHECKBOX);
}

/**
 * Whether this **group** should not take layout space (used for `section-item` `display: none`).
 * Delegates visibility rules to `shouldHideFieldOrSection` for fields/sections; adds grouping rules
 * (e.g. short-text and radio groups are hidden only when **every** option is hidden).
 */
export function isSectionOrFieldGroupHidden(
    group: SectionOrFieldGroupDto,
    selectedRequest: Request,
    odvetvieId: number,
    fieldRelationEffects: RequestFieldEffectDto[] = []
): boolean {
    const { groupFieldType, children } = group;
    // Odd empty group: treat as "not hidden" here; `sectionHasVisibleDescendants` still treats empty sections as no content.
    if (!children.length) return false;

    // One row with multiple text inputs, or stacked checkboxes that share a group:
    // hide the whole row only if each input is hidden.
    if (groupFieldType === RequestParameterTypeEnum.SHORT_TEXT || isCheckboxFieldGroup(group)) {
        const fields = children.map((c) => c.field).filter((f): f is RequestFieldDto => !!f);
        if (!fields.length) return false;
        return fields.every((f) => shouldHideFieldOrSection(f, selectedRequest, odvetvieId, fieldRelationEffects));
    }

    // Radio group: hide the whole block only when every option is hidden (options are filtered in the UI separately).
    if (groupFieldType === RequestParameterTypeEnum.RADIO_BUTTON) {
        const fields = children.map((c) => c.field).filter((f): f is RequestFieldDto => !!f);
        if (!fields.length) return false;
        return fields.every((f) => shouldHideFieldOrSection(f, selectedRequest, odvetvieId, fieldRelationEffects));
    }

    // Default group shape: one nested section or one field (see ComponentSwitcher.ResolveSectionOrField).
    const first = children[0];
    if (first.section) {
        // Hide if the section is hidden by business rules, or if it would render an empty body (no visible descendants).
        return (
            shouldHideFieldOrSection(first.section, selectedRequest, odvetvieId, fieldRelationEffects) ||
            !sectionHasVisibleDescendants(first.section, selectedRequest, odvetvieId, fieldRelationEffects)
        );
    }
    if (first.field) return shouldHideFieldOrSection(first.field, selectedRequest, odvetvieId, fieldRelationEffects);
    return false;
}

/**
 * Whether this **section** (and nested sections under it) has at least one field/group that will actually show.
 * Used to avoid rendering empty `.section` / `.component-switcher-holder` shells and to cooperate with
 * `isSectionOrFieldGroupHidden` for nested blocks (mutual recursion over the same visibility rules).
 */
export function sectionHasVisibleDescendants(
    section: RequestParameterSectionDto,
    selectedRequest: Request,
    odvetvieId: number,
    fieldRelationEffects: RequestFieldEffectDto[] = []
): boolean {
    if (!section.children?.length) return false;

    return section.children.some((group) => {
        // Skip groups that are fully hidden (including nested sections with no visible content).
        if (isSectionOrFieldGroupHidden(group, selectedRequest, odvetvieId, fieldRelationEffects)) {
            return false;
        }
        const first = group.children?.[0];
        if (first?.section) {
            // Nested section: need at least one visible descendant inside it, not only "section not hidden" on the parent.
            return sectionHasVisibleDescendants(first.section, selectedRequest, odvetvieId, fieldRelationEffects);
        }
        // Leaf field group (single field, short text row, radio row, etc.): already known visible from `isSectionOrFieldGroupHidden`.
        return true;
    });
}

export function shouldHideCategory(category: TaxonomyDto, selectedRequest: Request, odvetvieId: number, fieldRelationEffects: RequestFieldEffectDto[] = []) {
    let toHide = false;

    if (
        selectedRequest &&
        selectedRequest.itemsByGroup &&
        selectedRequest.fieldsByKey
    ) {
        if (category.linkedOdvetvie && category.linkedOdvetvie.length > 0) {
            toHide = true;

            if (odvetvieId && odvetvieId !== 0) {
                for (let x = 0; x < category.linkedOdvetvie.length; x++) {
                    if (
                        category.linkedOdvetvie[x].linkedOdvetvieId ===
                        odvetvieId &&
                        category.linkedOdvetvie[x].linkedOdvetvieWhen === 1
                    ) {
                        toHide = false;
                    }
                }

                if (!toHide) {
                    for (let x = 0; x < category.linkedOdvetvie.length; x++) {
                        if (
                            category.linkedOdvetvie[x].linkedOdvetvieId ===
                            odvetvieId &&
                            category.linkedOdvetvie[x].linkedOdvetvieWhen === 2
                        ) {
                            toHide = true;
                        }
                    }
                }
            } else {
                toHide = true;
            }
        }

        if (!toHide) {
            if (
                category.linkedButtons &&
                category.linkedButtons.length > 0 &&
                category.linkedButtons[0].linkedButtonKey !== null
            ) {
                toHide = true;

                for (let x = 0; x < category.linkedButtons.length; x++) {
                    let field =
                        selectedRequest.fieldsByKey[
                        category.linkedButtons[x].linkedButtonKey
                        ];

                    if (
                        field && field.group &&
                        !shouldHideFieldOrSection(
                            selectedRequest.sections[
                            selectedRequest.sections.findIndex(
                                (x) => x.id === field.parentSectionId
                            )
                            ],
                            selectedRequest,
                            odvetvieId,
                            fieldRelationEffects
                        )
                    ) {
                        let group = selectedRequest.itemsByGroup[field.group];

                        // groups are made only for radiobuttons -> fieldtype = 9 (getFieldsFromSection)
                        if (group && group.groupValue) {
                            if (
                                group.groupValue ===
                                category.linkedButtons[x].linkedButtonKey &&
                                category.linkedButtons[x].linkedButtonWhen === 1
                            ) {
                                toHide = false;
                            }
                        }
                    }
                }

                if (!toHide) {
                    for (let x = 0; x < category.linkedButtons.length; x++) {
                        let field =
                            selectedRequest.fieldsByKey[
                            category.linkedButtons[x].linkedButtonKey
                            ];

                        if (
                            field && field.group &&
                            !shouldHideFieldOrSection(
                                selectedRequest.sections[
                                selectedRequest.sections.findIndex(
                                    (x) => x.id === field.parentSectionId
                                )
                                ],
                                selectedRequest,
                                odvetvieId,
                                fieldRelationEffects
                            )
                        ) {
                            let group =
                                selectedRequest.itemsByGroup[field.group];

                            // groups are made only for radiobuttons -> fieldtype = 9 (getFieldsFromSection)
                            if (group && group.groupValue) {
                                if (
                                    group.groupValue ===
                                    category.linkedButtons[x]
                                        .linkedButtonKey &&
                                    category.linkedButtons[x]
                                        .linkedButtonWhen === 2
                                ) {
                                    toHide = true;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    return toHide;
}

/**
 * Parses an ISO date string as UTC.
 * If the date string doesn't have timezone information (no 'Z' suffix or timezone offset),
 * it appends 'Z' to force UTC parsing.
 * This ensures dates from the backend (which are stored in UTC) are correctly parsed as UTC in the frontend.
 * 
 * @param dateString - ISO date string from the backend
 * @returns Date object parsed as UTC
 */
export function parseUtcDate(dateString: string | null | undefined): Date | null {
    if (!dateString) {
        return null;
    }

    // If the string already has timezone info (ends with 'Z' or has timezone offset like +02:00), parse directly
    if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
        return new Date(dateString);
    }

    // Otherwise, append 'Z' to force UTC parsing
    return new Date(dateString + 'Z');
}
